/**
 * When a user first visits the site call geoip detect endpoint
 * to determine if the language dialog should be shown
 */

import renderToString from "preact-render-to-string";
import Cookies from "js-cookie";

const DEFAULT_LOCALE = "en-150";

type LanguageDialogProps = {
  country_name: string;
  country_iso_code: string;
  url: string;
};

type languageUrlsProps = {
  [key: string]: string;
};

class LanguageDialog {
  static initEvents() {
    document.addEventListener("DOMContentLoaded", LanguageDialog.detectLanguage);
  }

  static detectLanguage() {
    if (LanguageDialog.getDismissedCookie() === "true") {
      return;
    }

    // Get language urls from language switcher element
    const switcher = document.getElementById("language-switcher");

    if (switcher === null) {
      throw new Error("Language switcher element not found");
    }

    // Can be used to determine the suggested url
    const languageUrls: languageUrlsProps = JSON.parse(switcher.dataset.languageUrls ?? "{}");

    fetch("/wp-json/class/v1/geoipdetect/", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    }).then((data) => {
      const currentLanguage = document.getElementsByTagName("html")[0].getAttribute("lang") ?? DEFAULT_LOCALE;

      const continent = data.result.continent.code;
      const country = data.result.country.iso_code;

      const isEU = continent === "EU";
      const isUS = country === "US";
      const isJP = false; //country === "JP";

      /** Check if the current language is the same as the detected language */
      if (isEU && currentLanguage === "en-150") {
        // User is in Europe and language is already set to en-150
        // Do nothing
        return;
      }
      if (isUS && currentLanguage === "en-US") {
        // User is in the US and language is already set to en-US
        // Do nothing
        return;
      }
      if (isJP && currentLanguage === "ja") {
        // User is in Japan and language is already set to ja
        // Do nothing
        return;
      }
      if (!isEU && !isUS && !isJP) {
        // User is not in Europe, US, or Japan
        // Do nothing
        return;
      }

      const dialogHTML = LanguageDialog.renderLanguageDialog({
        url: languageUrls[isEU ? "en-150" : isUS ? "en-US" : "ja"],
        country_name: (data.result.country.names.en === "United Kingdom" ? "the " : "") + data.result.country.names.en,
        country_iso_code: isUS || isJP ? country : continent,
      });
      const footerElement = document.getElementById("footer") ?? document.createElement("div");
      footerElement.insertAdjacentHTML("afterend", dialogHTML);

      // Add event listener to dialog button to dismiss the language dialog
      const dialogElement = document.getElementById("language-dialog") ?? document.createElement("div");
      const dismissBtn = dialogElement.querySelector("[data-action='close']") ?? document.createElement("button");

      dismissBtn.addEventListener("click", (event) => {
        event.preventDefault();
        LanguageDialog.setDismissedCookie();
        dialogElement.remove();
      });

      // alert(JSON.stringify(data));
    }).catch((error) => {
      console.error("Error:", error);
    });
  }

  static getDismissedCookie() {
    return Cookies.get("language_dialog_dismissed");
  }

  static setDismissedCookie(value = "true") {
    Cookies.set("language_dialog_dismissed", value, { expires: 7 });
  }

  // Generate the language dialog markup
  static renderLanguageDialog(props: LanguageDialogProps) {
    const { country_name, country_iso_code, url } = props;

    const TITLE_TEXT = `Looks like your visiting from ${country_name}`;
    const COPY_TEXT = (
      <>
        <p>{`Would you like to visit the ${country_iso_code} site.`}</p>
        <p>{"If you choose to \"Stay Here\" you can change the site by using the language switcher in the menu."}</p>
      </>
    );
    const LEAVE_BTN_TEXT = `${country_iso_code} Website`;
    const DISMISS_BTN_TEXT = "Stay Here";

    const modal = (
      <dialog id="language-dialog" className="modal modal-language-dialog" open>
        <div className="modal__dialog">
          <div className="modal__content">
            <div className="modal__header">
              <h2 className="h4 modal__title">{TITLE_TEXT}</h2>
              <div className="modal__copy">
                {COPY_TEXT}
              </div>
            </div>
            <ul className="list--inline">
              <li><a href={url} className="btn btn-secondary">{LEAVE_BTN_TEXT}</a></li>
              <li>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-action="close"
                >
                  {DISMISS_BTN_TEXT}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </dialog>
    );

    return renderToString(modal);
  }
}

LanguageDialog.initEvents();

export default LanguageDialog;
